import { HttpBackend, HttpClient, HttpHeaders, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HotToastService } from '@ngxpert/hot-toast';
import { forkJoin } from 'rxjs';

import { AppInitValues } from '@app/app-init-values';
import { StorageService } from '@common/storage.service';
import { environment } from '@environments/environment';
import { IdentityClientService } from '@identity/identity-client.service';
import { IdentityConstants } from '@identity/identity-constants.service';
import { NotificationService } from '@shared/services/notification.service';

const IDENTITY_URL = environment.identityAPI;

@Injectable({
    providedIn: 'root'
})
export class ConfigService {
    private httpBackend = inject(HttpBackend);
    private storageService = inject(StorageService);
    private http = inject(HttpClient);
    private notification = inject(HotToastService);
    private notificationService = inject(NotificationService);

    loadConfig(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const client = window.location.pathname.split('/')[1] || '';

            let headers = new HttpHeaders();
            headers = headers.append('Content-Type', 'application/json');
            headers = headers.append('Access-Control-Allow-Origin', '*');
            headers = headers.append('Access-Control-Allow-Methods', 'POST,PATCH,OPTIONS');
            headers = headers.append('X-Product', AppInitValues.PRODUCT);
            headers = headers.append('X-Service', AppInitValues.INITIAL_SERVICE);

            this.httpBackend.handle(new HttpRequest('GET', `${IDENTITY_URL}/api/client/get?url=${client}`, headers))
                .subscribe({
                    next: (clientInfo: any) => {
                        const clientCode = clientInfo.body?.code ?? null;

                        if (clientCode !== null) {
                            const currentClient = new IdentityClientService(new IdentityConstants()).get();

                            if (Object.keys(currentClient).length === 0 || currentClient.code !== clientInfo.body?.code) {
                                this.storageService.set('sas-auth-client', clientCode);
                                this.storageService.set('sas-auth-current-client', JSON.stringify(clientInfo.body));
                            }
                            this.storageService.set('sas-auth-product', AppInitValues.PRODUCT);
                            this.storageService.set('sas-auth-service', AppInitValues.INITIAL_SERVICE);

                            headers = headers.append('X-Client', clientCode);

                            const clientConfig = this.http.get(`${IDENTITY_URL}/api/client/configuration`, { headers });
                            const serviceConfig = this.http.get(`${IDENTITY_URL}/api/service/configuration`, { headers });

                            forkJoin({ clientConfig, serviceConfig }).subscribe({
                                next: ({ clientConfig, serviceConfig }: any) => {
                                    if (!serviceConfig?.length) {
                                        this.notification.error(`HUB service not configured for: ${clientInfo.body.name}`);
                                        reject(`HUB service not configured for: ${clientInfo.body.name}`);
                                    }

                                    this.storageService.set('sas-auth-client-configuration', JSON.stringify(clientConfig));
                                    this.storageService.set('sas-auth-service-configuration', JSON.stringify(serviceConfig));

                                    resolve();
                                },
                                error: (err: any) => {
                                    this.notificationService.error(err, 'UNHANDLED_ERROR');
                                    console.error('Error loading configuration:', err);
                                }
                            });
                        }
                    },
                    error: (err: any) => {
                        this.notification.error(err.error[0]?.message ?? err.error.message);
                    }
                });
        });
    }

    getModuleConfig(client: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.httpBackend.handle(new HttpRequest('GET', `${IDENTITY_URL}/api/client/get?url=${client}`))
                .subscribe({
                    next: (clientInfo: any) => {
                        const clientCode = clientInfo.body?.code ?? null;

                        if (clientCode !== null) {
                            this.http.get(`${IDENTITY_URL}/api/service/configuration`, {})
                                .subscribe({
                                    next: (data: any) => {
                                        this.storageService.set('sas-auth-service-configuration', JSON.stringify(data));
                                        resolve();
                                    },
                                    error: (err: any) => console.error('Error loading configuration:', err)
                                });
                        }
                    },
                    error: (err: any) => {
                        this.notification.error(err.error[0].message ?? err.error.message);
                    }
                });
        });
    }
}
